import {
  SFStack,
  SFTypography,
  SFProgressBar,
  SFUseStyled,
  SFTextField,
} from "@sf-core";
import { useTranslation } from "react-i18next";
import { MODAL_TYPES } from "../../constants/modalTypes";
import { RateButton, CourseButtons, SkillpathButtons  } from "./modalButtons";

const { styled } = SFUseStyled();

const StyledCourseImage = styled("div")(({ imageSource }) => ({
  width: "200px",
  height: "105px",
  borderRadius: "3.065px",
  backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.3) -28.07%, rgba(0, 0, 0, 0.6) 100%), url(${imageSource})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  boxShadow: "0px 6.13095px 24.52381px 0px rgba(0, 0, 0, 0.80)",
}));

const StyledStack = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "marginBottom",
})(({ marginBottom, theme }) => ({
  width: "515px",
  marginRight: theme.spacing(3),
  ...(marginBottom && {
    marginBottom: theme.spacing(5),
  }),
}));

const StyledTextField = styled(SFTextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: theme.palette.grey100.main,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.2) !important",
  },
}));

const CourseDetails = ({modal, isLastCourse, labels, progressMessage}) => {
  const { t } = useTranslation();
  return (
    <>
      {isLastCourse ? (
        // If it is a course modal and it is the last course render complete progress bar
        <>
          <StyledStack direction="column" spacing={1} marginBottom={true}>
            <SFTypography variant="body2">{t(progressMessage)}</SFTypography>
            <SFTypography variant="progressBarLabel">
              {t(labels.progressLabel, {
                completed: modal.completedCourses,
                total: modal.totalCourses,
              })}
            </SFTypography>
            <SFProgressBar value={100} narrow />
          </StyledStack>
        </>
      ) : (
        // If it's not the last course render progress bar and next course thumbnail
        <>
          <StyledStack direction="row" spacing={5}>
            <SFStack direction="column" spacing={2.5}>
              <SFTypography variant="body2">{t(progressMessage)}</SFTypography>
              <SFStack direction="column" spacing={1}>
                <SFTypography variant="progressBarLabel">
                  {t(labels.progressLabel, {
                    completed: modal.completedCourses,
                    total: modal.totalCourses,
                  })}
                </SFTypography>
                <div style={{ width: "240px", marginBottom: "100px" }}>
                  <SFProgressBar
                    value={(modal.completedCourses / modal.totalCourses) * 100}
                    narrow
                  />
                </div>
              </SFStack>
            </SFStack>
            <div style={{ width: "120px" }}>
              <SFStack direction="column" spacing={2.5}>
                <SFTypography variant="body2">
                  {t(labels.nextLabel)}
                </SFTypography>
                <StyledCourseImage imageSource={modal.nextCourseThumbnail}>
                  <SFStack
                    alignItems="center"
                    justifyContent="center"
                    style={{ height: "100%", textAlign: "center" }}
                  >
                    <SFTypography variant="body2">
                      {modal.nextCourseName}
                    </SFTypography>
                  </SFStack>
                </StyledCourseImage>
              </SFStack>
            </div>
          </StyledStack>
        </>
      )}
    </>
  );
};

const ModalActions = ({
  modal,
  labels,
  submitRating,
  feedbackText,
  setFeedbackText,
  popupClose,
}) => {
  const { t } = useTranslation();

  const isSkillpathModal = modal.type == MODAL_TYPES.SKILLPATH;
  const isCourseModal = modal.type == MODAL_TYPES.CONTENT;
  const progressMessage = modal.isAcademyCourse
    ? labels?.progressSkillspathMessage
    : labels?.progressDomainMessage;
  const isLastCourse = modal.completedCourses === modal.totalCourses;

  return (
    // Check if it is a course completion modal to render the course details or not
    <SFStack direction="column" spacing={5}>
      {isCourseModal ? (
        <CourseDetails
          modal={modal}
          isLastCourse={isLastCourse}
          labels={labels}
          progressMessage={progressMessage}
        />
      ) : (
        //If it is a skillpath or job domain modal render the feedback text field
        <StyledStack>
          <StyledTextField
            multiline
            minRows={5}
            maxRows={5}
            value={feedbackText}
            onChange={({ target: { value } }) => setFeedbackText(value)}
            maxLength={300}
            placeholder={t(labels?.feedback)}
          />
        </StyledStack>
      )}
      {/* Buttons for the modals */}
      <StyledStack direction="row" justifyContent="space-between">
        {
          // Check if it is the last course of the course modal or it is a skillpath or job domain modal
          isLastCourse ? (
            //If it is a Skillpath modal render share and download certificate buttons else render Finish & Rate button
            <>
              {isSkillpathModal && (
                <SkillpathButtons modal={modal} labels={labels} />
              )}
              <RateButton
                labels={labels}
                submitRating={submitRating}
                popupClose={popupClose}
              />
            </>
          ) : (
            // if it is course modal and it'not the last course render close and next course buttons
            <CourseButtons
              labels={labels}
              submitRating={submitRating}
              popupClose={popupClose}
            />
          )
        }
      </StyledStack>
    </SFStack>
  );
};

export { ModalActions };